/**
 * Pure derivations for the Balances / Positions panels. Display math only (JS numbers); order sizing must
 * keep using trading/rules.ts decimals. Outcome ids are parsed by hl/outcomes.ts `fromCoin` only.
 */
import { coin as outcomeCoin, fromCoin, sideName, type Side } from '../hl/outcomes';
import type {
  AllMidsResponse,
  ClearinghouseState,
  OutcomeMetaResponse,
  OutcomeSpec,
  QuestionSpec,
  SpotBalance,
  SpotClearinghouseState,
} from '../hl/types';
import { availableUsdc } from '../trading/rules';
import { outcomeTitle } from '../describe';

function num(s: string | null | undefined): number {
  if (s === null || s === undefined || s === '') return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function numOrNull(s: string | null | undefined): number | null {
  if (s === null || s === undefined || s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export interface UsdcBalance {
  total: number;
  hold: number;
  available: number;
}

export interface OutcomeHolding {
  /** Balance coin, "+<enc>". */
  balanceCoin: string;
  /** Market-data coin, "#<enc>". */
  marketCoin: string;
  outcome: number;
  side: Side;
  sideName: string;
  title: string;
  shares: number;
  /** Shares locked by resting sells. */
  hold: number;
  /** USDC cost basis. */
  entryNtl: number;
  /** entryNtl / shares. */
  avgEntry: number | null;
  mid: number | null;
  /** shares * mid. */
  value: number | null;
  /** value - entryNtl. */
  upnl: number | null;
  /** upnl / entryNtl. */
  upnlPct: number | null;
}

export interface OtherBalance {
  coin: string;
  total: number;
  hold: number;
  entryNtl: number;
}

export interface DerivedBalances {
  usdc: UsdcBalance;
  outcomes: OutcomeHolding[];
  other: OtherBalance[];
  /** Sum of outcome values that have a mid. */
  outcomeValue: number;
  outcomeUpnl: number;
}

/** Legacy zero rows like "o458" (STREAMS.md S2 Q1). */
const LEGACY_O_ROW = /^o\d+$/;

export interface MetaIndex {
  outcomes: Map<number, OutcomeSpec>;
  /** outcome id -> the categorical question it belongs to. */
  questions: Map<number, QuestionSpec>;
}

export function metaIndex(meta: OutcomeMetaResponse | undefined): MetaIndex {
  const outcomes = new Map<number, OutcomeSpec>();
  const questions = new Map<number, QuestionSpec>();
  for (const o of meta?.outcomes ?? []) outcomes.set(o.outcome, o);
  for (const q of meta?.questions ?? []) {
    for (const id of [q.fallbackOutcome, ...(q.namedOutcomes ?? []), ...(q.settledNamedOutcomes ?? [])]) {
      if (typeof id === 'number') questions.set(id, q);
    }
  }
  return { outcomes, questions };
}

export function toOutcomeHolding(
  row: SpotBalance,
  metas: MetaIndex,
  mids: AllMidsResponse | undefined,
): OutcomeHolding | null {
  if (!row.coin.startsWith('+')) return null;
  const ref = fromCoin(row.coin);
  if (!ref) return null;
  const shares = num(row.total);
  if (shares === 0) return null;
  const entryNtl = num(row.entryNtl);
  const marketCoin = outcomeCoin(ref.outcome, ref.side);
  const mid = numOrNull(mids?.[marketCoin]);
  const meta = metas.outcomes.get(ref.outcome);
  const value = mid === null ? null : shares * mid;
  const upnl = value === null ? null : value - entryNtl;
  return {
    balanceCoin: row.coin,
    marketCoin,
    outcome: ref.outcome,
    side: ref.side,
    sideName: meta ? sideName(meta, ref.side) : ref.side === 0 ? 'Yes' : 'No',
    title: outcomeTitle(meta, ref.outcome, metas.questions.get(ref.outcome)),
    shares,
    hold: num(row.hold),
    entryNtl,
    avgEntry: entryNtl / shares,
    mid,
    value,
    upnl,
    upnlPct: upnl !== null && entryNtl > 0 ? upnl / entryNtl : null,
  };
}

export function deriveBalances(
  state: SpotClearinghouseState | undefined,
  meta: OutcomeMetaResponse | undefined,
  mids: AllMidsResponse | undefined,
): DerivedBalances {
  const rows = state?.balances ?? [];
  const usdcRow = rows.find((r) => r?.coin === 'USDC');
  const usdc: UsdcBalance = {
    total: num(usdcRow?.total),
    hold: num(usdcRow?.hold),
    available: num(availableUsdc(rows)),
  };
  const metas = metaIndex(meta);
  const outcomes: OutcomeHolding[] = [];
  const other: OtherBalance[] = [];
  for (const row of rows) {
    if (typeof row?.coin !== 'string' || typeof row.total !== 'string') continue;
    if (row.coin === 'USDC' || LEGACY_O_ROW.test(row.coin)) continue;
    if (row.coin.startsWith('+')) {
      const h = toOutcomeHolding(row, metas, mids);
      if (h) outcomes.push(h);
      continue;
    }
    if (num(row.total) === 0) continue;
    other.push({ coin: row.coin, total: num(row.total), hold: num(row.hold), entryNtl: num(row.entryNtl) });
  }
  outcomes.sort(
    (a, b) => (b.value ?? b.entryNtl) - (a.value ?? a.entryNtl) || a.balanceCoin.localeCompare(b.balanceCoin),
  );
  other.sort((a, b) => b.entryNtl - a.entryNtl || a.coin.localeCompare(b.coin));
  let outcomeValue = 0;
  let outcomeUpnl = 0;
  for (const o of outcomes) {
    outcomeValue += o.value ?? 0;
    outcomeUpnl += o.upnl ?? 0;
  }
  return { usdc, outcomes, other, outcomeValue, outcomeUpnl };
}

export interface PerpPositionRow {
  coin: string;
  /** Signed size (szi). */
  size: number;
  entryPx: number;
  /** positionValue / |szi| (HL values positions at mark). */
  markPx: number | null;
  positionValue: number;
  upnl: number;
  roe: number;
  leverageType: string;
  leverage: number;
  liqPx: number | null;
  marginUsed: number;
  fundingSinceOpen: number;
}

export interface PerpAccountSummary {
  accountValue: number;
  totalNtlPos: number;
  totalMarginUsed: number;
  maintenanceMargin: number;
  withdrawable: number;
  /** Sum of uPnL over open positions. */
  upnl: number;
}

export interface DerivedPerps {
  positions: PerpPositionRow[];
  summary: PerpAccountSummary | null;
}

export function derivePerps(state: ClearinghouseState | undefined): DerivedPerps {
  if (!state) return { positions: [], summary: null };
  const positions: PerpPositionRow[] = [];
  for (const ap of state.assetPositions ?? []) {
    const p = ap?.position;
    if (!p || typeof p.coin !== 'string') continue;
    const size = num(p.szi);
    if (size === 0) continue;
    const positionValue = num(p.positionValue);
    positions.push({
      coin: p.coin,
      size,
      entryPx: num(p.entryPx),
      markPx: positionValue / Math.abs(size),
      positionValue,
      upnl: num(p.unrealizedPnl),
      roe: num(p.returnOnEquity),
      leverageType: p.leverage?.type ?? 'cross',
      leverage: p.leverage?.value ?? 0,
      liqPx: numOrNull(p.liquidationPx),
      marginUsed: num(p.marginUsed),
      fundingSinceOpen: num(p.cumFunding?.sinceOpen),
    });
  }
  positions.sort((a, b) => b.positionValue - a.positionValue || a.coin.localeCompare(b.coin));
  const ms = state.marginSummary;
  return {
    positions,
    summary: {
      accountValue: num(ms?.accountValue),
      totalNtlPos: num(ms?.totalNtlPos),
      totalMarginUsed: num(ms?.totalMarginUsed),
      maintenanceMargin: num(state.crossMaintenanceMarginUsed),
      withdrawable: num(state.withdrawable),
      upnl: positions.reduce((s, p) => s + p.upnl, 0),
    },
  };
}
